import { useMemo } from 'react';
import { message } from 'antd';
import { T, useTranslate } from '@tolgee/react';
import StatusButton from 'components/StatusButton';
import { getTalkInfo, updateConversationV2 } from 'services/api/conversation.convList';
import { useMakeConversationCache } from 'hooks/business/conversation';
import { searchOrderRef } from 'views/Conversation/ConvList/SearchPageItem';
import NonAssistLimitTip from '../NonAssistLimitTip';
import { useMemoizedFn } from 'ahooks';

export interface MessageEditorDisableProps {
  conversation: Conversation.Conversation;
  forceDisableText?: string;
  forceAcquireControlReason?: Conversation.ForceAcquireControlReason;
}

export interface WaConvStatusResult {
  text?: string;
  canSendTemplate: boolean;
}

export const useWaConvStatus = (conversation: Conversation.Conversation) => {
  const { t } = useTranslate();

  const waConvStatus = useMemo((): WaConvStatusResult => {
    const { channel, waConvInfo, waUserInfo } = conversation;
    let text: string | undefined;
    const canSendTemplate = false;
    const noLimit =
      waConvInfo?.existUserMsg && waConvInfo?.existConv && waUserInfo?.replyStatus === 'AGREE';
    if (channel !== 'WA_BIZ' || noLimit) {
      return {
        text,
        canSendTemplate: true,
      };
    }

    if (waUserInfo?.regStatus === 'UNREGISTERED') {
      return {
        text: t('scene.chatWorkspace.whatsAppTemplate.hint.notRegistered'),
        canSendTemplate,
      };
    }
    if (waUserInfo?.replyStatus === 'DISAGREE') {
      return {
        text: t('scene.chatWorkspace.whatsAppTemplate.hint.buyerRejected'),
        canSendTemplate,
      };
    }
    if (!waUserInfo?.optInStatus) {
      return {
        text: t('scene.chatWorkspace.whatsAppTemplate.hint.firstTemplateOnly'),
        canSendTemplate: true,
      };
    }
    if (waUserInfo?.replyStatus === 'NO_REPLY') {
      return {
        text: t('scene.chatWorkspace.whatsAppTemplate.hint.cannotSend'),
        canSendTemplate,
      };
    }
    if (!waConvInfo?.existUserMsg) {
      return {
        text: t('scene.chatWorkspace.whatsAppTemplate.hint.templateOnly'),
        canSendTemplate: true,
      };
    }

    return {
      text,
      canSendTemplate,
    };
  }, [conversation, t]);

  return { waConvStatus };
};

/**
 * 消息编辑器禁用态与蒙层文案
 * 单一职责：根据渠道、接客权、拉黑、SIP、WhatsApp/美客多规则计算 disableText、waConvStatus、抢接客回调
 */
export function useMessageEditorDisable(props: MessageEditorDisableProps) {
  const { conversation: currentConversation, forceDisableText, forceAcquireControlReason } = props;
  const { conversationId, channel } = currentConversation;
  const { t } = useTranslate();
  const setConversation = useMakeConversationCache('set');
  const { waConvStatus } = useWaConvStatus(currentConversation);

  const handleAcquireClick = useMemoizedFn(() => {
    const _forceAcquireControlReason =
      forceAcquireControlReason || (searchOrderRef.current ? 'SEARCH_ORDER' : undefined);
    return updateConversationV2({
      conversationId,
      sourceAction: _forceAcquireControlReason
        ? (
            {
              PROCESS_TODO: 'TODO',
              PROCESS_REFUND_OR_RETURN: 'ORDER_MANAGEMENT',
              PROCESS_RATING: 'RATING',
              SEARCH_ORDER: 'SEARCH_ORDER',
            } as const
          )[_forceAcquireControlReason]
        : undefined,
      forceAcquireControl: true,
      forceAcquireControlReason: _forceAcquireControlReason,
    })
      .then(() => {
        return getTalkInfo({
          conversationId,
        }).then(setConversation);
      })
      .catch(() => {
        message.error(t('common.feedback.operationFailed'));
      });
  });

  const disableText = useMemo(() => {
    if (forceDisableText) return forceDisableText;

    const {
      isAffiliateSip,
      blacklistFlag,
      canBeSent,
      controlStatus,
      nonAssistLimit = false,
      crossFulfillment,
    } = currentConversation;

    if (crossFulfillment) {
      return ' ';
    }

    if (nonAssistLimit) {
      return <NonAssistLimitTip conversation={currentConversation} />;
    }

    if (isAffiliateSip) {
      return `${t('scene.chatWorkspace.messageEditor.disabled.sipSubStore')}\n${t(
        'scene.chatWorkspace.messageEditor.disabled.storeOutsideAgentGroup',
      )}`;
    }
    if (blacklistFlag) {
      return t('scene.chatWorkspace.messageEditor.disabled.buyerBlocked');
    }
    if (channel === 'LAZADA' && !canBeSent) {
      return t('scene.chatWorkspace.messageEditor.disabled.lazadaProactiveMessageLimit');
    }
    if (channel === 'MERCADO' && !canBeSent) {
      return (
        <T
          keyName="美客多发送消息限制提示"
          params={{
            br: <br />,
          }}
        />
      );
    }

    if (!controlStatus) {
      const receptionName =
        currentConversation.receptionAssistantName || currentConversation.receptionName;

      if (receptionName) {
        return (
          <div>
            <T
              keyName="conversation.chatEditor.disabled.hint.occupied"
              params={{
                agentName: receptionName,
                btn: <StatusButton type="link" size="small" onClick={handleAcquireClick} />,
              }}
            />
          </div>
        );
      }

      return (
        <div>
          <T
            keyName="conversation.chatEditor.disabled.hint.unassigned"
            params={{
              btn: <StatusButton type="link" size="small" onClick={handleAcquireClick} />,
            }}
          />
        </div>
      );
    }
    if (waConvStatus.text) {
      return waConvStatus.text;
    }
    return '';
  }, [currentConversation, channel, t, waConvStatus, forceDisableText, handleAcquireClick]);

  return {
    disableText,
    waConvStatus,
    handleAcquireClick,
  };
}
